/* Affichage des cadeaux au panier — Scoop by Coralie (V17→V23, réduit au lot 104).

   RÉSULTAT ATTENDU
   ----------------
   Ce fichier PEINT. Il n'écrit plus rien dans le panier, n'interroge plus le
   réseau et ne décide plus d'aucun cadeau : il écoute `sbc:cadeaux`, émis par
   le moteur unique `assets/sbc-panier.js`, et met le bloc de progression en
   accord avec l'état reçu.

   POURQUOI — ce qui a été retiré au lot 104
   -----------------------------------------
   Ce fichier portait à la fois la règle métier, les requêtes, la coordination
   par minuteries, un wrapper de `window.fetch`, un patch de
   `XMLHttpRequest.prototype` et la peinture. Les quatre premiers rôles sont
   partis :
   · la règle est devenue `assets/sbc-panier-decision.js`, fonction pure testée
     par `node tools/verif-panier.test.mjs` ;
   · les requêtes et l'ordre des opérations sont au moteur, qui les sérialise ;
   · le wrapper `fetch` et le patch XHR n'existent plus : plus aucun script du
     thème n'observe les requêtes des autres, ni celles des applications
     tierces (Judge.me et les avis passent désormais sans être vus) ;
   · les minuteries de coordination (250 ms, 120 ms au retour d'onglet, 150 ms
     au focus) ont disparu avec elles.

   Ce qui reste ici, et rien d'autre : la phrase d'état et le jalon de la
   livraison.

   Règles métier rappelées (`CLAUDE.md` § 3, décision D19 du 12 septembre
   2026, révisée par D20 le 16 septembre 2026) : plus aucun carnet offert —
   le lot 147 l'a retiré partout — et livraison offerte dès 100 € pour la
   France, la Belgique et Monaco. La vidéo de préparation reste hors de cette
   jauge : c'est une option de chaque scoop choisie sur la fiche produit,
   payante à 1, 2 et 3 scoops, offerte à partir de 4 (D20) — un état de
   variante, jamais un montant de panier, donc jamais un jalon ici.
   UN seul jalon, donc. Le seuil vient de
   `snippets/sbc-cadeaux-config.liquid`.
*/
(() => {
  const noeudConfig = document.querySelector('[data-sbc-cadeaux-config]');
  if (!noeudConfig) return;

  let CFG;
  try { CFG = JSON.parse(noeudConfig.textContent); } catch (_) { return; }
  if (!CFG || !Array.isArray(CFG.produitsEligibles)) return;

  const SEUIL_LIVRAISON = CFG.livraisonOfferteEligible === true ? (Number(CFG.seuilLivraisonCentimes) || 0) : 0;
  /* Une « livraison offerte » ne se dit jamais sans nommer les pays
     (CLAUDE.md § 3, A-03) : la valeur vient du même réglage que la bannière
     et la fiche produit (`settings.free_shipping_zones`), jamais recopiée. */
  const LIVRAISON_ZONES = (CFG.livraisonZones || '').trim();
  const promotionRelais = () => window.SbcPanierDecision?.promotionLivraisonRelais(CFG, undefined, Date.now()) === true;
  /* L'état canonique vient du moteur ; ce fichier ne le calcule jamais. */
  let etatPublic = { sousTotal: 0, nombreScoops: 0, palierLivraison: false };

  const eur = (c) => (c / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
  /* Lot 130 — sous un jalon large de 88 px, « dès 63,00 € » passe à la ligne.
     Un seuil rond s'écrit donc sans centimes ; un seuil qui en a les garde. */
  const eurCourt = (c) => (c % 100 === 0
    ? (c / 100).toLocaleString('fr-FR') + ' €'
    : eur(c));
  const dejaVu = { livraison: null };

  /* Phrase d'état — V19, réduite au lot 148, corrigée en clôture de la vague
     147-150.

     Une seule phrase, toujours vraie. Elle annonce le seul palier restant
     depuis D19 — la livraison, en euros — puis le constate franchi. Le repli
     « Tous vos avantages sont acquis » promettait un pluriel d'avantages qui
     n'existe plus. Et une « livraison offerte » ne se dit jamais sans nommer
     les pays (CLAUDE.md § 3) : sans zones configurées, la phrase se tait. */
  const messageEtat = (st, SL) => {
    if (promotionRelais()) return '✓ Livraison offerte sur le mode éligible pour votre pays.';
    if (!(SL > 0) || !LIVRAISON_ZONES) return '';
    if (st < SL) return `Encore ${eur(SL - st)} pour atteindre la livraison offerte.`;
    return `✓ Seuil de livraison offerte atteint.`;
  };

  /* Lot 163 (ordre d'Alban, 17 septembre 2026) — les libellés visibles des
     jalons (`LIBELLES`, nom + seuil écrits dans une vignette) disparaissent
     avec la vignette elle-même : le camion ne porte plus de nom ni de seuil,
     seulement l'état lu par `[data-gift-state]` (plus bas) et le mot fixe du
     réglage `free_shipping_label`, déjà rendu par le Liquid. */

  const peindreCadeaux = () => {
    const bloc = document.querySelector('[data-sbc-gifts]');
    /* Lot 148 — la garde portait sur le seuil vidéo, qui n'existe plus : c'est
       désormais le seuil de livraison qui conditionne la peinture du bloc.
       Sans seuil configuré, il n'y a aucun palier à montrer. */
    if (!bloc || !SEUIL_LIVRAISON) return;
    const q = (sel) => bloc.querySelector(sel);
    const st = etatPublic.sousTotal || 0;
    const SL = SEUIL_LIVRAISON;
    const promoRelais = promotionRelais();
    const livraisonAcquise = promoRelais || (etatPublic.palierLivraison && st >= SL);

    bloc.hidden = false;

    /* ------------------------------------------------------------------
       V19.2 — échelle par segments égaux, réduite à un seul segment au
       lot 148.

       Chaque palier occupe un segment de largeur égale ; il n'en reste qu'un
       depuis D19, celui de la livraison, qui occupe donc toute la barre et
       pose son jalon à 100 %. Le remplissage suit la même échelle : il atteint
       exactement le jalon au moment où le palier tombe, et pas avant.
       ------------------------------------------------------------------ */
    /* Le palier porte sa base et son point de départ : la livraison mesure le
       total du panier en centimes, et son segment part de zéro. Le remplissage
       est une proportion, jamais une somme. */
    const paliersConfigures = [
      ['livraison', SL, st, 0]
    ].filter(([, seuil]) => seuil > 0);

    const segment = paliersConfigures.length ? 100 / paliersConfigures.length : 100;

    let pct = 100;
    for (let index = 0; index < paliersConfigures.length; index += 1) {
      const [, seuil, base, depart] = paliersConfigures[index];
      if (base >= seuil) continue;
      const etendue = seuil - depart;
      const part = etendue > 0 ? (base - depart) / etendue : 0;
      pct = index * segment + Math.max(0, Math.min(1, part)) * segment;
      break;
    }
    pct = promoRelais ? 100 : Math.max(0, Math.min(100, pct));

    const remplissage = q('[data-gifts-fill]');
    if (remplissage) remplissage.style.width = pct.toFixed(4) + '%';

    const jauge = q('[data-gifts-gauge]');
    if (jauge) jauge.setAttribute('aria-valuenow', String(Math.round(pct)));

    /* V19.1 — repère du montant, accroché à l'extrémité du remplissage.

       Aux deux bords, le centrer le ferait sortir du tiroir : on bascule alors
       son ancrage (`--tx`) et on décale sa pointe (`--pointe`) pour qu'elle
       continue de désigner la même position. Au-delà du dernier palier, le
       repère reste à 100 % mais le montant affiché demeure le montant réel. */
    const bulle = q('[data-gifts-bulle]');
    if (bulle) {
      bulle.style.setProperty('--x', pct.toFixed(4) + '%');
      let ancrage = '-50%';
      let pointe = '50%';
      if (pct < 12) { ancrage = '0%'; pointe = `${Math.max(8, pct * 2)}%`; }
      else if (pct > 88) { ancrage = '-100%'; pointe = `${Math.min(92, 100 - (100 - pct) * 2)}%`; }
      bulle.style.setProperty('--tx', ancrage);
      bulle.style.setProperty('--pointe', pointe);
      const montant = q('[data-gifts-total]');
      if (montant) montant.textContent = eur(st);
    }

    /* Un seul jalon depuis le lot 148. `pending` signifie : le seuil est
       atteint côté thème mais l'avantage n'est pas (encore) constaté — l'état
       est alors énoncé, jamais présenté comme acquis. */
    const jalons = [
      ['livraison', SL, livraisonAcquise, livraisonAcquise]
    ];

    /* Hiérarchie dynamique : le prochain avantage est le premier jalon
       configuré qui n'est pas encore acquis. Lui seul est agrandi ; ceux qui
       le précèdent sont réduits avec une coche, ceux qui le suivent sont
       réduits et atténués. Quand tout est acquis, aucun jalon ne domine. */
    const prochain = (jalons.find(([, seuil, , acquis]) => seuil > 0 && !acquis) || [])[0] || null;
    if (bulle) bulle.classList.toggle('is-complet', prochain === null);
    /* Lot 163 (ordre d'Alban, 17 septembre 2026) — l'état posé sur la RACINE
       du bloc : la CSS y accroche la couleur du remplissage, du camion et de
       l'étiquette d'un seul coup (`.sbc-prog[data-atteint] …`), plutôt que sur
       chaque élément séparément. */
    bloc.toggleAttribute('data-atteint', Boolean(livraisonAcquise));

    jalons.forEach(([cle, seuil, palier, acquis]) => {
      const item = q(`[data-gift="${cle}"]`);
      if (!item) return;
      if (seuil <= 0) { item.hidden = true; return; }
      item.hidden = false;

      const etat = acquis ? 'done' : (palier ? 'pending' : 'locked');
      /* L'éclat ne se joue qu'au véritable franchissement, jamais à chaque
         rendu Ajax : on compare à l'état mémorisé du cycle précédent. */
      if (dejaVu[cle] !== null && dejaVu[cle] !== undefined && dejaVu[cle] !== etat && etat === 'done') {
        item.classList.add('just-unlocked');
        setTimeout(() => item.classList.remove('just-unlocked'), 600);
      }
      dejaVu[cle] = etat;
      item.dataset.state = etat;

      /* Un palier franchi dont la gratuité n'est pas encore constatée n'est
         pas un cadeau acquis : `aria-busy` prévient les lecteurs d'écran
         qu'il change encore. */
      const enCours = !acquis && Boolean(palier);
      item.toggleAttribute('aria-busy', enCours);
      /* Lot 163 — le camion ne porte plus ni nom ni seuil en texte visible
         (le mot fixe du réglage `free_shipping_label` s'en charge, rendu par
         le Liquid) : seul l'état lu par les lecteurs d'écran reste ici, avec
         les pays quand ils sont configurés (CLAUDE.md § 3). */
      const texte = item.querySelector('[data-gift-state]');
      if (texte) {
        texte.textContent = promoRelais
          ? 'Livraison offerte sans minimum jusqu’au 16 novembre inclus, sur le mode éligible pour votre pays.'
          : LIVRAISON_ZONES
          ? `Seuil de livraison offerte dès ${eurCourt(seuil)} (sous conditions) : ${acquis ? 'atteint' : 'à atteindre'}`
          : `Palier ${acquis ? 'atteint' : 'à atteindre'}`;
      }
    });

    const message = messageEtat(st, SL);
    const phrase = q('[data-gifts-message]');
    if (phrase) phrase.textContent = message;
    /* Clôture de la vague 147-150 — la jauge ne mesure plus que des euros :
       aucun avantage DE PANIER ne dépend du nombre de scoops (la vidéo, qui
       en dépend depuis D20, est un état de variante lu sur la fiche, pas un
       jalon de cette jauge). Le citer ici laissait croire à un second
       compteur. */
    if (jauge) jauge.setAttribute('aria-valuetext', `${eur(st)} d'achats. ${message}`.trim());

  };

  const rafraichir = () => {
    peindreCadeaux();
  };

  /* Le moteur annonce un panier stabilisé et l'état des cadeaux. C'est le seul
     déclencheur : plus de minuterie, plus d'observation des requêtes.

     Clôture de la vague 147-150 — le narrateur de gestes du lot 130
     (`phraseDuGeste`, son minuteur de 8 s, l'état et le panier du cycle
     précédent, la ligne `[data-gifts-alerte]`) est retiré : depuis les lots
     147 et 148 le thème n'ajoute, ne retire et ne remplace plus aucune ligne,
     `phraseDuGeste` renvoyait toujours la chaîne vide et l'alerte ne pouvait
     plus rien afficher. Il ne restait qu'une mémoire entretenue pour personne.
     Si un geste automatique revenait un jour, il se dirait de nouveau ici —
     mais il faudrait d'abord qu'il existe. */
  document.addEventListener('sbc:cadeaux', (event) => {
    const detail = event.detail || {};
    if (detail.etat) etatPublic = detail.etat;
    rafraichir();
  });

  /* La section panier vient d'être remplacée : le bloc peint est neuf, il faut
     le repeindre avec l'état déjà connu. */
  document.addEventListener('sbc:section-rendue', rafraichir);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', rafraichir, { once: true });
  } else {
    rafraichir();
  }

  /* Exposé pour le tiroir, qui repeint après avoir injecté une copie fraîche
     de la section. */
  window.sbcCadeaux = {
    etat: () => ({ ...etatPublic }),
    peindre: rafraichir,
    reconcilier: () => (window.SbcPanier ? window.SbcPanier.sync() : Promise.resolve(null))
  };
})();
